import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, HStack, Text, useToast, VStack } from "@chakra-ui/react";
import { PROVIDER_PAGES } from "../../base/router/pages";
import { useNavigate, useParams } from "react-router-dom";
import { FiArrowLeft, FiPlus, FiRefreshCw, FiUserPlus } from "react-icons/fi";
import { StyledTabBar } from "../../components/StyledTabBar/StyledTabBar";
import { ProvidersTable } from "./components/ProvidersTable";
import { useGetGroup, useLeaveGroup, useSyncGroupSchedule } from "../../api/GroupApi";
import { useHandleModal } from "../../hooks/useHandleModal";
// import { GroupTargetedPaymentSwitch } from "./components/GroupTargetedPaymentSwitch";
import AdminCompletedStudiesTable from "./components/AdminCompletedStudiesTable/AdminCompletedStudiesTable";
import CYAlertsTable from "./components/CYAlertsTable/CYAlertsTable";
import { timeZoneFullNames } from "../../utils/timeZones";
import { CYModal } from "../../components/CYModal/CYModal";
import { WorklistType } from "../../types/Worklist";
import { useActivateCodeYellow } from "../../api/CodeYellowApi";
import { GroupBalanceView } from "../../components/GroupBalanceView/GroupBalanceView";
import { InviteRADModal } from "../../components/InviteRadModal/InviteRADModal.tsx";
import TemporaryPasswordModal from "../../components/InviteRadModal/TemporaryPasswordModal.tsx";
import { useInviteUserToGroup } from "../../api/GroupProviderApi";
import SummaryReport from "./components/SummaryReport";
import { useLnqRights } from "../../hooks/lnq/useLnqRights.ts";
import CPTSettings from "./components/CPTSettings.tsx";

export const GroupDetailsPage = () => {
  const navigate = useNavigate();
  const params = useParams<{ groupId: string }>();
  const groupId = params.groupId ?? "";
  const { data } = useGetGroup(groupId);
  const { mutate: leaveGroup } = useLeaveGroup();
  const handleModal = useHandleModal();
  const [groupTimeZone, setGroupTimeZone] = useState("");
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const { mutate: activateCodeYellow, status } = useActivateCodeYellow();
  const { mutate: syncSchedule, status: syncStatus } = useSyncGroupSchedule(groupId);
  const toast = useToast();
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isTemporaryPasswordModalOpen, setIsTemporaryPasswordModalOpen] = useState(false);
  const [temporaryPassword, setTemporaryPassword] = useState("");
  const { mutateAsync: inviteProvider } = useInviteUserToGroup();
  const { hasLnqRights } = useLnqRights();

  useEffect(() => {
    const updateTime = () => {
      if (data?.group?.timeZone) {
        const fullTimeZoneName = timeZoneFullNames[data.group.timeZone];
        setGroupTimeZone(`${fullTimeZoneName}`);
      }
    };

    updateTime();
    const timer = setInterval(updateTime, 60000); // Update every minute

    return () => clearInterval(timer);
  }, [data?.group?.timeZone]);

  const navigateBack = () => {
    navigate(PROVIDER_PAGES.groups);
  };

  const leaveThisGroup = () => {
    if (groupId) {
      leaveGroup(groupId);
      navigateBack();
    }
  };

  const handleOpenInviteModal = () => {
    setIsInviteModalOpen(true);
  };

  const handleCloseInviteModal = () => {
    setIsInviteModalOpen(false);
  };

  const handleInviteConfirm = async (data: {
    firstName: string;
    lastName: string;
    email: string;
    npi: string;
  }) => {
    try {
      const response = await inviteProvider({
        groupId: groupId,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        npi: data.npi,
      });

      if (response.password) {
        setTemporaryPassword(response.password);
        setIsTemporaryPasswordModalOpen(true);
      }

      toast({
        title: "Invitation Sent",
        description: "The provider has been successfully invited.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      handleCloseInviteModal();
    } catch (error: unknown) {
      toast({
        title: "Error Inviting Provider",
        description:
          error instanceof Error ? error.message : "An unexpected error occurred.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };
  const handleCloseTemporaryPasswordModal = () => {
    setIsTemporaryPasswordModalOpen(false);
  };

  const tappedLeaveGroup = (_e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    handleModal({
      title: "Are you sure you want to leave this group?",
      leftButtonTitle: "Cancel",
      rightButtonTitle: "Confirm",
      onClickRightButton: () => {
        leaveThisGroup();
      },
    });
  };

  const navigateToEditGroup = () => {
    navigate(`${PROVIDER_PAGES.groups}/edit/${groupId}`);
  };

  const handleAddCodeYellow = useCallback(() => {
    setIsGroupModalOpen(true);
  }, []);

  const handleGoToSchedule = () => {
    navigate(`${PROVIDER_PAGES.schedule.replace(":groupId?", groupId ?? "")}`);
  };

  const handleActivateCodeYellow = useCallback(
    (
      distributionType: "open" | "target",
      rvu: number,
      userIDs?: string[],
      groupId?: string,
      dateRange?: { start: string; end?: string },
      limits?: { amountLimit?: number; RVUsLimit?: number },
    ) => {
      if (!groupId || !data?.group?.worklistId) {
        console.error("Missing groupId or worklistId");
        toast({
          title: "Error Creating LnQ",
          description: "Missing required information to create LnQ.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        setIsGroupModalOpen(false);
        return;
      }

      activateCodeYellow(
        {
          worklistId: data.group.worklistId,
          distributionType,
          usdPerRvu: rvu,
          userIds: userIDs,
          groupId,
          dateRange,
          limits,
        },
        {
          onSuccess: () => {
            setIsGroupModalOpen(false);
            toast({
              title: "LnQ Created",
              description: "The LnQ has been successfully created.",
              status: "success",
              duration: 5000,
              isClosable: true,
            });
          },
          onError: (error: Error) => {
            console.error("Error creating LnQ:", error);
            toast({
              title: "Error Creating LnQ",
              description: error.message || "An unexpected error occurred.",
              status: "error",
              duration: 5000,
              isClosable: true,
            });
          },
        },
      );
    },
    [activateCodeYellow, data?.group?.worklistId, toast, setIsGroupModalOpen],
  );

  const handleSyncSchedule = useCallback(() => {
    if (typeof groupId !== "string") return;

    syncSchedule(groupId, {
      onSuccess: () => {
        toast({
          title: "Schedule Synced",
          description: "The schedule has been successfully synced.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      },
      onError: (error: Error) => {
        toast({
          title: "Error Syncing Schedule",
          description: error.message || "An unexpected error occurred.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      },
    });
  }, [groupId, syncSchedule, toast]);

  return (
    <>
      <Box mt={"24px"} mb={"10px"}>
        <HStack justify={"space-between"}>
          <Button
            leftIcon={<FiArrowLeft />}
            variant="link"
            onClick={navigateBack}
            color="white"
          >
            My Groups
          </Button>
          {data?.isAdmin ? (
            <HStack>
              <Button
                leftIcon={<FiPlus />}
                colorScheme="yellow"
                variant="solid"
                onClick={handleAddCodeYellow}
                isDisabled={status === "pending" || !hasLnqRights}
                isLoading={status === "pending"}
              >
                Create LnQ
              </Button>
              <Button
                variant="outline"
                colorScheme="brandYellow"
                onClick={handleGoToSchedule}
              >
                View Schedule
              </Button>
              <Button
                leftIcon={<FiRefreshCw />}
                variant="outline"
                colorScheme="brandYellow"
                onClick={handleSyncSchedule}
                isDisabled={syncStatus === "pending"}
                isLoading={syncStatus === "pending"}
              >
                Sync Schedule
              </Button>
              <Button
                leftIcon={<FiUserPlus />}
                variant="outline"
                colorScheme="brandYellow"
                onClick={handleOpenInviteModal}
              >
                Invite
              </Button>
              <Button
                variant="outline"
                colorScheme="brandYellow"
                onClick={navigateToEditGroup}
              >
                Edit Group
              </Button>
              {/*<GroupTargetedPaymentSwitch group={data.group} />*/}
            </HStack>
          ) : (
            <HStack>
              <Button
                variant="outline"
                colorScheme="brandYellow"
                onClick={handleGoToSchedule}
              >
                View Schedule
              </Button>
              <Button
                variant="outline"
                colorScheme="brandYellow"
                onClick={tappedLeaveGroup}
              >
                Leave Group
              </Button>
            </HStack>
          )}
        </HStack>
        <VStack align="start" spacing={0}>
          <Text fontSize="3xl" fontWeight="700" color="white">
            {data?.group?.facilityName}
          </Text>
          <Text fontSize="16px" color="gray.500">
            {data?.group?.description}
          </Text>
          <Text fontSize="16px" color="gray.500">
            {groupTimeZone}
          </Text>
        </VStack>
      </Box>
      <StyledTabBar
        size="lg"
        tabList={[
          { value: "providers", label: "Providers" },
          ...(data?.isAdmin
            ? [
                { value: "completedStudies", label: "Completed Studies" },
                { value: "cyAlerts", label: "LnQ Alerts" },
                { value: "balance", label: "Balance" },
                { value: "summaryReport", label: "Summary Report" },
                { value: "cptSettings", label: "CPT Settings" },
              ]
            : []),
        ]}
        tabPanels={[
          ...(data?.group
            ? [
                <ProvidersTable
                  key="providers"
                  group={data?.group}
                  isGroupAdmin={data?.isAdmin}
                />,
              ]
            : []),
          ...(data?.isAdmin
            ? [
                <AdminCompletedStudiesTable
                  groupId={groupId}
                  groupName={data?.group?.facilityName}
                  key="completedStudies"
                />,
                <CYAlertsTable groupId={groupId} key="cyAlerts" />,
                <GroupBalanceView groupId={groupId} key="balance" />,
                <SummaryReport groupId={groupId} key="summaryReport" />,
                <CPTSettings groupId={groupId} key="cptSettings" />,
              ]
            : []),
        ]}
      />
      <CYModal
        type={WorklistType.GROUP}
        isOpen={isGroupModalOpen}
        onClose={() => setIsGroupModalOpen(false)}
        activate={handleActivateCodeYellow}
        groupName={data?.group?.facilityName}
        groupId={groupId}
      />
      <InviteRADModal
        isOpen={isInviteModalOpen}
        onClose={handleCloseInviteModal}
        onConfirm={handleInviteConfirm}
      />
      <TemporaryPasswordModal
        isOpen={isTemporaryPasswordModalOpen}
        onClose={handleCloseTemporaryPasswordModal}
        temporaryPassword={temporaryPassword}
        isReset={false}
      />
    </>
  );
};
